import { t } from 'i18n';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Row,
  Col,
  styled,
  Body,
  ThemeProps,
  Divider,
  CustomLink,
} from '@teko/ds-desktop';
import {
  LocationOutlined,
  ChecklistOutlined,
  PromoOutlined,
  OrderOutlined,
} from '@teko/ds-icon';
import { useTenantConfig } from 'contexts';
import { useLiteMode, useRegionPicker, useLoyalty } from 'hooks';
import {
  CartOverview,
  LogoHeader,
  SearchBar,
  UserProfileHeader,
  NotificationOverview,
} from 'components/desktop/organisms';
import { PopupRegionPicker } from 'components/desktop/organisms';
import { MenuItem } from '@teko/discovery';
import { getTrackingData, MegaMenu, TOPBAR_HEIGHT } from '@teko/teko-ecommerce';
import { useAuth } from '@teko/auth-react';
import { convertMegaMenuItem, getNavItems } from 'helpers';
import { NavigationButton } from 'components/desktop/organisms/style';
import { useRouter } from 'next/router';

interface WithLiteProps {
  lite: boolean;
  verticalNavItem: boolean;
}
const StyledNavigationBar = styled.div<WithLiteProps>(
  ({ lite, verticalNavItem }) => `
  @media print {
    display: none;
  }
  background-color: #fff;
  position: sticky;
  top: 0;
  z-index: 1040;
  box-shadow: 0 0.5rem 1rem 0 rgba(0, 0, 0, 0.1);
  @media screen and (max-width: 767px) {
    height: auto !important;
  }

  ${
    lite && verticalNavItem
      ? `
      min-width: 68px;
      .title {
        visibility: hidden;
        height: 0;
      }
      .button {
        gap: 0;
      }
    `
      : ``
  }
`,
);
const NavItem = styled.div(
  ({ theme }: ThemeProps) => `
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  cursor: pointer;
  &:hover {
    div {
      color: ${theme.colors.primary500} !important;
    }
    path {
      fill: ${theme.colors.primary500} !important;
    }
  }
`,
);
const Overlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 550;
  background: rgba(0, 0, 0, 0.5);
  -webkit-transition-delay: 0.1s;
  transition-delay: 0.1s;
`;
interface DisplayConfigInterface {
  order?: boolean;
  promotion?: boolean;
  login?: boolean;
  cart?: boolean;
  additional?: React.ReactNode;
}
interface NavigationBarProps {
  showShippingFeeMiniCart?: boolean;
  menuItems?: MenuItem[];
  liteLogo?: boolean;
  displayConfig?: DisplayConfigInterface;
}

const isVerticalNavItem = (tenant: string) => {
  switch (tenant) {
    case 'phongvu':
      return true;
    case 'ipp':
      return true;
    default:
      return false;
  }
};
const defaultDisplayConfig: DisplayConfigInterface = {
  order: false,
  promotion: false,
  login: true,
  cart: true,
  additional: undefined,
};

export const NavigationBar: React.FunctionComponent<NavigationBarProps> = ({
  showShippingFeeMiniCart = true,
  menuItems,
  liteLogo,
  displayConfig,
}: NavigationBarProps) => {
  displayConfig = { ...defaultDisplayConfig, ...displayConfig };
  const [navOffsetTop, setNavOffsetTop] = useState(0);
  const [navItems, setNavItems] = useState([]);
  const { isLite, dropdownOpen, toggle } = useLiteMode(liteLogo, navOffsetTop);
  const {
    partner,
    tenant,
    megaMenuType = 'vertical',
    alignMegaMenu = 'left',
    hideAccountInfo,
    notificationAppId,
  } = useTenantConfig();
  const regionPickerProps = useRegionPicker();
  const { user, authService } = useAuth();
  const onLogin = () => authService.login();
  const router = useRouter();
  const region = regionPickerProps?.activeLocation;
  const { loyaltyMemberInfo } = useLoyalty();

  const profileLinks = useMemo(() => {
    return getNavItems({
      tenant,
      logged: !!user?.userId,
      isLoyaltyMember: !!loyaltyMemberInfo,
      showNotificationItem: !!notificationAppId,
    });
  }, [tenant, user]);
  const promotionIcon = (
    <CustomLink href="/khuyen-mai" target="_blank">
      <NavigationButton
        className="button"
        style={
          isVerticalNavItem(tenant)
            ? { flexDirection: 'column' }
            : { flexDirection: 'row' }
        }
        {...getTrackingData({
          isTrackContent: true,
          regionName: 'headerBar',
          contentName: 'promotionLandingIcon',
        })}
      >
        <PromoOutlined size={36} color="textSecondary" />
        <span className="title">{t('Khuyến mãi')}</span>
      </NavigationButton>
    </CustomLink>
  );
  const orderIcon = (
    <NavigationButton
      className="button"
      onClick={(): void => {
        if (!user?.userId) {
          onLogin();
        } else {
          router.push('/account/orders');
        }
      }}
      style={
        isVerticalNavItem(tenant)
          ? { flexDirection: 'column' }
          : { flexDirection: 'row' }
      }
      {...getTrackingData({
        isTrackContent: true,
        regionName: 'headerBar',
        contentName: 'orderIcon',
      })}
    >
      <OrderOutlined size={36} color="textSecondary" />
      <span className="title">{t('Đơn hàng')}</span>
    </NavigationButton>
  );
  const loginIcon = (
    <UserProfileHeader
      vertical={isVerticalNavItem(tenant)}
      customProfile={
        !!partner ? (
          <CustomLink href="/account/orders">
            <NavigationButton className="button">
              <ChecklistOutlined size={36} color="textSecondary" />
              <div>{t('Quản lý đơn hàng')}</div>
            </NavigationButton>
          </CustomLink>
        ) : undefined
      }
      profileLinks={profileLinks}
    />
  );
  const notificationIcon = (
    <NotificationOverview title={isVerticalNavItem(tenant)} />
  );
  const cartIcon = (
    <CartOverview
      showShippingFeeMiniCart={showShippingFeeMiniCart}
      vertical={isVerticalNavItem(tenant)}
    />
  );
  const regionPickerIcon = (
    <NavItem
      onClick={() => {
        regionPickerProps.onOpenPopup();
      }}
      {...getTrackingData({
        isTrackContent: true,
        regionName: 'headerBar',
        contentName: 'location',
      })}
    >
      <LocationOutlined size={36} color="textSecondary" />
      <Body level={2} weight="medium" color="textSecondary">
        {t('Khu vực sinh sống')}
        <br />
        {region && region.label}
      </Body>
    </NavItem>
  );
  useEffect(() => {
    const getNavMenuByDisplayConfig = () => {
      const { order, promotion, login, cart, additional } = displayConfig;
      const navMenu = [];
      if (promotion) navMenu.push(promotionIcon);
      if (order) navMenu.push(orderIcon);
      if (login && !hideAccountInfo) navMenu.push(loginIcon);
      if (notificationAppId) navMenu.push(notificationIcon);
      if (cart) navMenu.push(cartIcon);
      if (region) navMenu.push(regionPickerIcon);
      if (additional) navMenu.push(additional);
      return navMenu;
    };
    setNavItems(getNavMenuByDisplayConfig());
  }, [user?.userId]);
  return (
    <>
      {dropdownOpen && <Overlay />}
      <StyledNavigationBar
        lite={isLite}
        verticalNavItem={isVerticalNavItem(tenant)}
        id="mainNavigationBar"
        ref={(navRef: HTMLDivElement) => {
          if (navRef) {
            const { scrollTop } = document.documentElement;
            setNavOffsetTop(scrollTop % TOPBAR_HEIGHT);
          }
        }}
      >
        <Container>
          <Row
            gutter={[16, 16]}
            align="middle"
            style={
              megaMenuType === 'vertical' && isVerticalNavItem(tenant) && isLite
                ? { height: '72px' }
                : { height: '88px' }
            }
          >
            <Col>
              <LogoHeader
                isLite={
                  megaMenuType === 'vertical' &&
                  (typeof window !== 'undefined' ? isLite : liteLogo)
                }
                dropdownOpen={dropdownOpen}
                toggle={toggle}
                // Only show mega menu dropdown on type vertical
                menuItems={megaMenuType === 'vertical' ? menuItems : []}
              />
            </Col>
            <Col flex="auto">
              <SearchBar />
            </Col>
            {navItems.map((item, index) => (
              <Col key={index}>{item}</Col>
            ))}
          </Row>
        </Container>
        {megaMenuType === 'horizontal' && (
          <>
            <Divider gap={0} color="divider" />
            <Container>
              <Row>
                <Col span={12}>
                  <MegaMenu
                    type="horizontal"
                    align={alignMegaMenu}
                    menuItems={menuItems.map(convertMegaMenuItem)}
                  />
                </Col>
              </Row>
            </Container>
          </>
        )}
      </StyledNavigationBar>
      <PopupRegionPicker {...regionPickerProps} />
    </>
  );
};
